// src/components/tenant/Gallery.tsx — a building's photos in the portal: a mosaic on a laptop (one large, four small,
// "All n photos" on the last), a sideways snap strip on a phone, each with its label; any one opens the full-screen
// viewer (arrows and ← / → keys, Esc to close, swipe on touch, the label and "3 / 12" at the foot).
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X, Images } from 'lucide-react';

export interface GalleryPhoto { url: string; thumbUrl: string; label: string | null; caption?: string | null }

export function TGallery({ photos, title }: { photos: GalleryPhoto[]; title: string }) {
  const [open, setOpen] = useState<number | null>(null);
  if (!photos.length) return null;
  const grid = photos.slice(0, 5);
  const more = photos.length - grid.length;
  return (
    <section className="t-rise" aria-label={`${title} — photos`}>
      {/* Laptop: the mosaic. */}
      <div className={`hidden gap-2.5 overflow-hidden rounded-[24px] md:grid ${grid.length >= 4 ? 'h-[420px] grid-cols-4 grid-rows-2' : grid.length === 3 ? 'h-[380px] grid-cols-3 grid-rows-2' : grid.length === 2 ? 'h-[340px] grid-cols-2' : 'h-[380px] grid-cols-1'}`} style={{ boxShadow: 'var(--shadow-card)' }}>
        {grid.map((p, i) => {
          const big = i === 0 && grid.length >= 3;
          const last = i === grid.length - 1 && photos.length > 1;
          return (
            <button key={p.url} type="button" onClick={() => setOpen(i)} aria-label={`Open photo ${i + 1} of ${photos.length}${p.label ? `: ${p.label}` : ''}`}
              className={`group relative overflow-hidden bg-ink-100 ${big ? 'col-span-2 row-span-2' : ''} ${grid.length === 4 && i === 3 ? 'col-span-2' : ''}`}>
              <img src={big || grid.length < 3 ? p.url : p.thumbUrl} alt={p.label ?? ''} loading="lazy" draggable={false}
                className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" />
              {p.label && <Label text={p.label} top={last} />}
              {last && (
                <span className="absolute bottom-3 right-3 inline-flex min-h-[40px] items-center gap-2 rounded-full bg-white/95 px-4 text-[13.5px] font-semibold text-ink-900 shadow-[var(--shadow-float)]">
                  <Images className="h-4 w-4 text-bronze-600" />{more > 0 ? `All ${photos.length} photos` : 'View photos'}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Phone: the snap strip, edge to edge. */}
      <div className="-mx-4 flex snap-x snap-mandatory gap-2.5 overflow-x-auto px-4 pb-1 md:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {photos.map((p, i) => (
          <button key={p.url} type="button" onClick={() => setOpen(i)} aria-label={`Open photo ${i + 1} of ${photos.length}${p.label ? `: ${p.label}` : ''}`}
            className="relative h-[220px] w-[82%] shrink-0 snap-center overflow-hidden rounded-[20px] bg-ink-100" style={{ boxShadow: 'var(--shadow-card)' }}>
            <img src={i === 0 ? p.url : p.thumbUrl} alt={p.label ?? ''} loading={i < 2 ? 'eager' : 'lazy'} draggable={false} className="h-full w-full object-cover" />
            {p.label && <Label text={p.label} />}
            <span className="absolute right-3 top-3 rounded-full bg-black/45 px-2.5 py-1 text-[12px] font-semibold text-white backdrop-blur">{i + 1} / {photos.length}</span>
          </button>
        ))}
      </div>

      {open !== null && <Viewer photos={photos} start={open} title={title} onClose={() => setOpen(null)} />}
    </section>
  );
}

function Label({ text, top }: { text: string; top?: boolean }) {
  return <span className={`absolute ${top ? 'top-3' : 'bottom-3'} left-3 max-w-[70%] truncate rounded-full bg-white/92 px-3 py-1.5 text-[12.5px] font-semibold text-ink-900 shadow-[0_2px_8px_rgba(26,22,16,0.12)]`}>{text}</span>;
}

/** The full-screen viewer. */
export function Viewer({ photos, start, title, onClose }: { photos: GalleryPhoto[]; start: number; title: string; onClose: () => void }) {
  const [i, setI] = useState(start);
  const n = photos.length;
  const go = useCallback((d: number) => setI((x) => (x + d + n) % n), [n]);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey, true);
    const was = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const back = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => { window.removeEventListener('keydown', onKey, true); document.body.style.overflow = was; back?.focus?.(); };
  }, [go, onClose]);
  // The neighbours load ahead so the next swipe is instant.
  useEffect(() => { for (const d of [1, -1]) { const p = photos[(i + d + n) % n]; if (p) { const im = new Image(); im.src = p.url; } } }, [i, n, photos]);

  const p = photos[i]!;
  const arrow = 'absolute top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/12 text-white backdrop-blur transition hover:bg-white/25 sm:grid';
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={`${title} — photo ${i + 1} of ${n}`} className="fixed inset-0 z-[80] flex flex-col text-white" style={{ background: "#120F0B" }}
      onTouchStart={(e) => { const t = e.touches[0]; if (t) touch.current = { x: t.clientX, y: t.clientY }; }}
      onTouchEnd={(e) => {
        const s = touch.current; const t = e.changedTouches[0]; touch.current = null;
        if (!s || !t) return;
        const dx = t.clientX - s.x; const dy = t.clientY - s.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
        else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) onClose();
      }}>
      <div className="safe-top flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <p className="truncate text-[15px] font-semibold">{title}</p>
          <p className="text-[13px] text-white/70" aria-live="polite">{i + 1} / {n}</p>
        </div>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Close photos" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/12 transition hover:bg-white/25">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 sm:px-20" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {n > 1 && <button type="button" onClick={() => go(-1)} aria-label="Previous photo" className={`${arrow} left-4`}><ChevronLeft className="h-6 w-6" /></button>}
        <img key={p.url} src={p.url} alt={p.label ?? `Photo ${i + 1}`} draggable={false} className="max-h-full max-w-full select-none rounded-[12px] object-contain" style={{ animation: 't-rise 260ms ease both' }} />
        {n > 1 && <button type="button" onClick={() => go(1)} aria-label="Next photo" className={`${arrow} right-4`}><ChevronRight className="h-6 w-6" /></button>}
      </div>

      <div className="px-4 pb-4 pt-3 text-center sm:px-6" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}>
        <p className="min-h-[22px] text-[15px] font-semibold">{p.label ?? ''}</p>
        {p.caption && <p className="mt-0.5 text-[13px] text-white/70">{p.caption}</p>}
        {n > 1 && (
          <div className="mx-auto mt-3 flex max-w-full justify-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
            {photos.map((x, k) => (
              <button key={x.url} type="button" onClick={() => setI(k)} aria-label={`Photo ${k + 1}`} aria-current={k === i ? 'true' : undefined}
                className={`h-12 w-16 shrink-0 overflow-hidden rounded-[8px] ring-2 transition ${k === i ? 'opacity-100 ring-[#CDAE89]' : 'opacity-55 ring-transparent hover:opacity-90'}`}>
                <img src={x.thumbUrl} alt="" className="h-full w-full object-cover" draggable={false} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
